import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../ui/select';
import { Button } from "../ui/button";
import {
    FileText,
    Download,
    Check,
    Loader2,
    Info,
    Search,
    Filter,
    RefreshCcw,
    FileSpreadsheet,
    FileDown,
    ChevronDown
} from 'lucide-react';
import { Input } from '../ui/input';
import { maintenanceService } from '../../services/maintenance/planMaestroService';
import type { PlanMaestro, ItemCatalogo } from '../../types/planMaestro';
import toast from 'react-hot-toast';
import { cn } from '../../lib/utils';

export default function PlanesMaestros() {
    const [planes, setPlanes] = useState<PlanMaestro[]>(() => {
        try {
            const cached = sessionStorage.getItem('vilox_cache_list_planes');
            return cached ? JSON.parse(cached) : [];
        } catch { return []; }
    });

    // Inicializar con el ID del caché o el primero de la lista para evitar el mensaje de "Seleccione un plan"
    const [selectedPlanId, setSelectedPlanId] = useState<string>(() => {
        const lastSelected = sessionStorage.getItem('vilox_cache_last_plan_id');
        if (lastSelected) return lastSelected;
        return planes.length > 0 ? planes[0].id.toString() : '';
    });

    const [planDetalle, setPlanDetalle] = useState<any>(null);
    const [loading, setLoading] = useState(planes.length === 0);
    const [loadingPlan, setLoadingPlan] = useState(false);
    const [exportingExcel, setExportingExcel] = useState(false);
    const [exportingPdf, setExportingPdf] = useState(false);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const exportMenuRef = useRef<HTMLDivElement>(null);

    // Filtros
    const [searchTerm, setSearchTerm] = useState('');
    const [operationFilter, setOperationFilter] = useState('ALL');

    // 🧠 Caché local para evitar recargas constantes del mismo plan - Persistente en sesión
    const [cachePlanes, setCachePlanes] = useState<Record<string, any>>(() => {
        try {
            const stored = sessionStorage.getItem('vilox_cache_planes');
            return stored ? JSON.parse(stored) : {};
        } catch { return {}; }
    });

    // Guardar cambios en caché a sessionStorage
    useEffect(() => {
        try {
            sessionStorage.setItem('vilox_cache_planes', JSON.stringify(cachePlanes));
        } catch (e) {
            console.warn("SessionStorage full, cache not persisted", e);
        }
    }, [cachePlanes]);

    const fetchData = async (force = false) => {
        if (planes.length === 0 || force) setLoading(true);
        try {
            const result = await maintenanceService.getPlanesMaestros();
            setPlanes(result);
            sessionStorage.setItem('vilox_cache_list_planes', JSON.stringify(result));
            if (result.length > 0 && !selectedPlanId) {
                setSelectedPlanId(result[0].id.toString());
            }
        } catch (error) {
            console.error("Error cargando planes:", error);
            toast.error("Error al cargar lista de planes maestros");
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        // 1. Limpiar caché en sessionStorage
        sessionStorage.removeItem('vilox_cache_list_planes');
        sessionStorage.removeItem('vilox_cache_planes');
        
        // 2. Limpiar estados de caché local
        setCachePlanes({});
        
        // 3. Si hay un plan seleccionado, forzar su recarga inmediata
        if (selectedPlanId) {
             setLoadingPlan(true);
             try {
                 const response = await maintenanceService.getPlanMaestroById(parseInt(selectedPlanId));
                 if (response.success && (response.data || response.plan)) {
                     const data = response.data || {
                         plan: response.plan,
                         rutinas: response.rutinas,
                         items: response.items,
                         detalles: response.detalles
                     };
                     setPlanDetalle(data);
                     setCachePlanes({ [selectedPlanId]: data });
                 }
             } catch (error) {
                 console.error("Error recargando detalle:", error);
             } finally {
                 setLoadingPlan(false);
             }
        }

        // 4. Recargar la lista de planes maestros
        await fetchData(true);
        toast.success("Datos actualizados");
    };


    const handleExportExcel = async () => {
        if (!selectedPlanId) { toast.error('Selecciona un plan primero'); return; }
        setExportingExcel(true);
        setShowExportMenu(false);
        try {
            const response = await maintenanceService.exportVehiclePlanDetailsExcel(selectedPlanId);
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const planName = planDetalle?.plan?.nombre || planDetalle?.nombre || `plan_${selectedPlanId}`;
            link.download = `Plan_Mantenimiento_${planName.replace(/\s+/g, '_')}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('✅ Excel generado correctamente');
        } catch (error) {
            console.error('Error exportando Excel:', error);
            toast.error('❌ Error al generar el Excel. El backend puede no soportar esta operación aún.');
        } finally {
            setExportingExcel(false);
        }
    };

    const handleExportPdf = async () => {
        if (!selectedPlanId) { toast.error('Selecciona un plan primero'); return; }
        setExportingPdf(true);
        setShowExportMenu(false);
        try {
            const response = await maintenanceService.exportVehiclePlanDetailsPdf(selectedPlanId);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const planName = planDetalle?.plan?.nombre || planDetalle?.nombre || `plan_${selectedPlanId}`;
            link.download = `Plan_Mantenimiento_${planName.replace(/\s+/g, '_')}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('✅ PDF generado correctamente');
        } catch (error) {
            console.error('Error exportando PDF:', error);
            toast.error('❌ Error al generar el PDF. El backend puede no soportar esta operación aún.');
        } finally {
            setExportingPdf(false);
        }
    };

    // Cerrar menú al hacer click fuera
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
                setShowExportMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredPlanes = React.useMemo(() => {
        return planes.filter(p => {
            const matchesSearch = p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.modelo_vehiculo?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesOperation = operationFilter === 'ALL' || p.tipo_operacion === operationFilter;
            return matchesSearch && matchesOperation;
        });
    }, [planes, searchTerm, operationFilter]);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (!selectedPlanId) return;

        // Guardar la última selección para navegación instantánea
        sessionStorage.setItem('vilox_cache_last_plan_id', selectedPlanId);

        // Si ya está en caché, lo usamos inmediatamente
        if (cachePlanes[selectedPlanId]) {
            setPlanDetalle(cachePlanes[selectedPlanId]);
            return;
        }

        const loadDetalle = async () => {
            setLoadingPlan(true);
            try {
                const response = await maintenanceService.getPlanMaestroById(parseInt(selectedPlanId));
                if (response.success && (response.data || response.plan)) {
                    const data = response.data || {
                        plan: response.plan,
                        rutinas: response.rutinas,
                        items: response.items,
                        detalles: response.detalles
                    };
                    setPlanDetalle(data);
                    // Guardar en caché local
                    setCachePlanes(prev => ({ ...prev, [selectedPlanId]: data }));
                }
            } catch (error) {
                console.error("Error cargando detalle:", error);
                toast.error("Error al cargar detalle del plan");
            } finally {
                setLoadingPlan(false);
            }
        };
        loadDetalle();
    }, [selectedPlanId]);

    // Extracción de datos del plan actual
    const plan = planDetalle?.plan || (planDetalle?.nombre ? planDetalle : null);
    const rutinas = planDetalle?.rutinas || [];
    const items = planDetalle?.items || [];
    const detalles = planDetalle?.detalles || {};

    // 🚀 Optimizando cálculos con useMemo
    const activeItems = React.useMemo(() => {
        if (!items || !rutinas || !detalles) return [];
        return (items || []).filter((item: ItemCatalogo) => {
            return rutinas.some((rutina: any) => {
                const detalle = detalles[rutina.id]?.find((d: any) =>
                    d.plan_item_id === item.id || d.item_catalogo_id === item.id
                );
                return detalle?.aplica;
            });
        });
    }, [items, rutinas, detalles]);

    const categories = React.useMemo(() =>
        Array.from(new Set(activeItems.map((i: ItemCatalogo) => i.categoria))),
        [activeItems]);

    const routineTotals = React.useMemo(() => {
        const totals: Record<number, number> = {};
        if (!rutinas) return totals;

        rutinas.forEach((rutina: any) => {
            if (!detalles[rutina.id]) {
                totals[rutina.id] = 0;
                return;
            }
            totals[rutina.id] = detalles[rutina.id].reduce((sum: number, det: any) => {
                if (!det.aplica) return sum;
                const item = items.find((i: any) => i.id === det.plan_item_id || i.id === det.item_catalogo_id);
                const price = item ? parseFloat(item.precio_unitario?.toString() || '0') : 0;
                return sum + (price * (det.cantidad || 0));
            }, 0);
        });
        return totals;
    }, [rutinas, detalles, items]);

    const grandTotal = React.useMemo(() =>
        Object.values(routineTotals).reduce((sum: number, total) => sum + total, 0),
        [routineTotals]);

    const calculateRoutineTotal = (rutinaId: number) => routineTotals[rutinaId] || 0;

    const getRutinaColor = (tipo: string) => {
        switch (tipo) {
            case 'M1': return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'M2': return 'bg-orange-100 text-orange-800 border-orange-200';
            case 'M3': return 'bg-red-100 text-red-800 border-red-200';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading && planes.length === 0) {
        return (
            <div className="flex h-full items-center justify-center min-h-[400px]">
                <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <span className="text-sm text-gray-500 font-medium">Dimensionando matriz de mantenimiento...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            {/* Header: Título y Selector */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex-1 min-w-0">
                    <h2 className="text-2xl font-bold text-gray-800 tracking-tight">
                        Plan de Mantenimiento
                    </h2>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                    {/* Buscador */}
                    <div className="relative w-full sm:w-[250px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                            placeholder="Buscar plan o modelo..."
                            className="pl-9 bg-white border-gray-200"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Filtro Operación */}
                    <Select value={operationFilter} onValueChange={setOperationFilter}>
                        <SelectTrigger className="w-full sm:w-[160px] bg-white">
                            <Filter className="h-4 w-4 mr-2 text-gray-400" />
                            <SelectValue placeholder="Operación" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="ALL">Todas las Ops</SelectItem>
                            <SelectItem value="CARRETERA">CARRETERA</SelectItem>
                            <SelectItem value="FUERA_CARRETERA">FUERA DE CARRETERA</SelectItem>
                            <SelectItem value="MIXTO">MIXTO</SelectItem>
                        </SelectContent>
                    </Select>

                    <div className="h-8 w-[1px] bg-gray-200 hidden sm:block mx-1"></div>

                    {/* Selector de Plan */}
                    <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
                        <SelectTrigger className="w-full md:w-[280px] bg-blue-50/50 border-blue-100 text-blue-900 font-medium">
                            <SelectValue placeholder="Seleccione un plan" />
                        </SelectTrigger>
                        <SelectContent>
                            {filteredPlanes.length === 0 ? (
                                <div className="p-2 text-center text-xs text-gray-400">No hay resultados</div>
                            ) : (
                                filteredPlanes.map(p => (
                                    <SelectItem key={p.id} value={p.id.toString()}>
                                        {p.nombre}
                                    </SelectItem>
                                ))
                            )}
                        </SelectContent>
                    </Select>

                    <div className="flex items-center gap-2 shrink-0">
                        {/* Botón Recargar */}
                        <Button 
                            variant="outline" 
                            onClick={handleRefresh}
                            disabled={loading || loadingPlan}
                            className="h-9 gap-2 border-gray-200 text-gray-600 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-colors"
                        >
                            <RefreshCcw className={cn("h-3.5 w-3.5", (loading || loadingPlan) && "animate-spin")} />
                            <span className="text-xs font-medium">Recargar</span>
                        </Button>

                        {/* Botón Exportar con dropdown */}
                        <div className="relative" ref={exportMenuRef}>
                            <Button
                                variant="outline"
                                onClick={() => setShowExportMenu(prev => !prev)}
                                disabled={!selectedPlanId || exportingExcel || exportingPdf}
                                className="h-9 gap-2 border-gray-200 text-gray-600 hover:bg-green-50 hover:text-green-700 hover:border-green-200 transition-colors"
                            >
                                {(exportingExcel || exportingPdf) ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <Download className="h-3.5 w-3.5" />
                                )}
                                <span className="text-xs font-medium">Exportar</span>
                                <ChevronDown className="h-3 w-3 text-gray-400" />
                            </Button>

                            {/* Dropdown menu */}
                            {showExportMenu && (
                                <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                                    <button
                                        onClick={handleExportExcel}
                                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors"
                                    >
                                        <FileSpreadsheet className="h-4 w-4 text-green-600" />
                                        <span className="font-medium">Descargar Excel</span>
                                    </button>
                                    <button
                                        onClick={handleExportPdf}
                                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors"
                                    >
                                        <FileDown className="h-4 w-4 text-red-600" />
                                        <span className="font-medium">Descargar PDF</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {
                loadingPlan || (loading && planes.length > 0) ? (
                    <div className="space-y-4">
                        <div className="h-32 bg-gray-50 animate-pulse rounded-xl border border-gray-100"></div>
                        <div className="h-64 bg-gray-50/50 animate-pulse rounded-xl border border-gray-100"></div>
                    </div>
                ) : plan ? (
                    <>
                        {/* Info Card */}
                        <Card className="p-4 bg-muted/20">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                    <span className="text-gray-500 block mb-1">Modelo Vehículo</span>
                                    <span className="font-semibold">{plan.modelo_vehiculo || 'N/A'}</span>
                                </div>
                                <div>
                                    <span className="text-gray-500 block mb-1">Motor</span>
                                    <span className="font-semibold">{plan.motor || 'N/A'}</span>
                                </div>
                                <div>
                                    <span className="text-gray-500 block mb-1">Transmisión</span>
                                    <span className="font-semibold">{plan.transmision || 'N/A'}</span>
                                </div>
                                <div>
                                    <span className="text-gray-500 block mb-1">Operación</span>
                                    <Badge variant={plan.tipo_operacion === 'CARRETERA' ? 'default' : 'secondary'}>
                                        {plan.tipo_operacion}
                                    </Badge>
                                </div>
                                <div className="border-l pl-4 border-gray-200 col-span-2 md:col-span-1">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-0.5 mt-1">Costo Total Ciclo</span>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-xs font-bold text-gray-400">S/</span>
                                        <span className="text-xl font-black text-blue-600">{grandTotal.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Matrix View */}
                        <div className="border rounded-lg shadow-sm overflow-hidden bg-white">
                            <div className="p-4 border-b bg-gray-50/50 flex flex-wrap gap-4 text-xs">
                                <div className="flex items-center gap-2">
                                    <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200 font-medium">M1</span>
                                    <span>Básico</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="px-2 py-0.5 rounded bg-orange-100 text-orange-800 border border-orange-200 font-medium">M2</span>
                                    <span>Intermedio</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="px-2 py-0.5 rounded bg-red-100 text-red-800 border border-red-200 font-medium">M3</span>
                                    <span>Mayor</span>
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left border-collapse">
                                    <thead className="bg-gray-50 text-gray-700 text-xs uppercase sticky top-0 z-20">
                                        <tr>
                                            <th className="sticky left-0 bg-gray-50 z-30 w-32 px-4 py-3 border-b text-left min-w-[120px]">Categoría</th>
                                            <th className="sticky left-[120px] bg-gray-50 z-30 w-64 px-4 py-3 border-b border-r text-left min-w-[250px]">Descripción</th>
                                            <th className="px-2 py-3 border-b text-center w-16">P. Unit</th>
                                            <th className="px-2 py-3 border-b text-center w-12">Cant.</th>
                                            <th className="px-2 py-3 border-b text-center w-14 border-r">Unid.</th>

                                            {(rutinas || []).map((rutina: any) => (
                                                <th key={rutina.id} className="px-2 py-3 border-b text-center min-w-[80px]">
                                                    <div className="flex flex-col items-center gap-1">
                                                        <span className={cn("px-1.5 py-0.5 rounded text-[10px] font-bold border", getRutinaColor(rutina.tipo))}>
                                                            {rutina.tipo}
                                                        </span>
                                                        <span className="text-[10px] text-gray-500 font-medium whitespace-nowrap">
                                                            {[
                                                                rutina.frecuencia_km ? `${rutina.frecuencia_km.toLocaleString()} KM` : null,
                                                                rutina.frecuencia_horas ? `${rutina.frecuencia_horas} HRS` : null,
                                                                rutina.frecuencia_meses ? `${rutina.frecuencia_meses} MESES` : null
                                                            ].filter(Boolean).join(' / ')}
                                                        </span>
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {(categories as string[]).map((category) => (
                                            <React.Fragment key={category}>
                                                {activeItems
                                                    .filter((i: ItemCatalogo) => i.categoria === category)
                                                    .map((item: ItemCatalogo, index: number, arr: any[]) => {
                                                        const isFirstInCategory = index === 0;
                                                        return (
                                                            <tr key={item.id} className="hover:bg-gray-50 transition-colors group">
                                                                {/* Categoría (Sticky) */}
                                                                {isFirstInCategory && (
                                                                    <td
                                                                        className="sticky left-0 z-10 bg-white px-4 py-3 font-medium text-purple-600 text-xs align-top border-r"
                                                                        rowSpan={arr.length}
                                                                    >
                                                                        <div className="sticky top-16">
                                                                            {category}
                                                                        </div>
                                                                    </td>
                                                                )}
                                                                {/* Si no es el primero, renderizamos una celda vacía invisible para mantener layout si fuera necesario, 
                                                                pero con rowspan no hace falta. Sin embargo, el sticky behavior requiere cuidado. 
                                                                Para simplificar en HTML sticky, si usamos rowspan la celda "ocupa" el espacio. 
                                                                Si no usamos rowspan repetimos el valor. 
                                                                En este caso, para simplificar sticky, repetiremos la celda pero con contenido visible solo en el primero 
                                                                o simplemente renderizamos siempre background blanco.
                                                            */}
                                                                {!isFirstInCategory && (
                                                                    <td className="sticky left-0 z-10 bg-white border-r hidden"></td>
                                                                    // Nota: Rowspan handlea esto, pero Tailwind sticky a veces falla con rowspan.
                                                                    // Estrategia alternativa: No usar rowspan para sticky columns, repetir valor o dejar vacío.
                                                                )}

                                                                {/* Descripción (Sticky) */}
                                                                <td className="sticky left-0 md:left-[120px] z-10 bg-white px-4 py-2 border-r group-hover:bg-gray-50">
                                                                    <div className="font-medium text-gray-800">{item.nombre}</div>
                                                                    <div className="text-xs text-gray-400">{item.codigo_interno}</div>
                                                                </td>

                                                                {/* Detalles estáticos */}
                                                                <td className="px-2 py-2 text-center text-gray-600 font-medium text-[11px]">
                                                                    S/ {parseFloat(item.precio_unitario?.toString() || '0').toFixed(2)}
                                                                </td>
                                                                <td className="px-2 py-2 text-center text-gray-600 font-medium text-xs">
                                                                    {(() => {
                                                                        const quantities = (rutinas || [])
                                                                            .map((r: any) => detalles[r.id]?.find((d: any) => d.plan_item_id === item.id || d.item_catalogo_id === item.id)?.cantidad)
                                                                            .filter((q: any) => q !== undefined && q !== null);

                                                                        if (quantities.length === 0) return '-';
                                                                        const total = quantities.reduce((sum: number, qty: number) => sum + Number(qty), 0);
                                                                        return total % 1 === 0 ? total : total.toFixed(2);
                                                                    })()}
                                                                </td>
                                                                <td className="px-2 py-2 text-center text-gray-500 border-r text-xs">{item.unidad_medida}</td>

                                                                {/* Checkmarks por rutina */}
                                                                {(rutinas || []).map((rutina: any) => {
                                                                    const detalle = detalles[rutina.id]?.find((d: any) => d.plan_item_id === item.id || d.item_catalogo_id === item.id);
                                                                    const aplica = detalle?.aplica;

                                                                    return (
                                                                        <td key={rutina.id} className="px-2 py-2 text-center border-r border-dashed last:border-r-0">
                                                                            {aplica ? (
                                                                                <div className="flex flex-col items-center gap-1.5 py-1">
                                                                                    <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-50 text-green-600 shadow-sm border border-green-100">
                                                                                        <Check className="h-3.5 w-3.5" />
                                                                                    </div>
                                                                                    <div className="flex flex-col text-[9px] leading-tight items-center">
                                                                                        <span className="font-bold text-slate-400 uppercase tracking-tighter">
                                                                                            {detalle.cantidad} {item.unidad_medida}
                                                                                        </span>
                                                                                        <span className="font-black text-blue-700 mt-0.5">
                                                                                            S/ {(Number(detalle.cantidad) * parseFloat(item.precio_unitario?.toString() || '0')).toFixed(2)}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            ) : (
                                                                                <span className="text-gray-100 text-xs">-</span>
                                                                            )}
                                                                        </td>
                                                                    );
                                                                })}
                                                            </tr>
                                                        );
                                                    })}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-slate-50 font-black border-t-2 border-slate-200">
                                        <tr>
                                            <td colSpan={2} className="px-4 py-4 text-right uppercase text-[10px] tracking-widest text-slate-400 border-r">Totales por Rutina</td>
                                            <td colSpan={3} className="bg-white border-r"></td>
                                            {rutinas.map((rutina: any) => (
                                                <td key={rutina.id} className="px-2 py-4 text-center border-r last:border-r-0">
                                                    <div className="flex flex-col items-center">
                                                        <span className="text-[9px] text-slate-400 uppercase font-bold mb-0.5">S/</span>
                                                        <span className="text-sm font-black text-blue-600">
                                                            {calculateRoutineTotal(rutina.id).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                                                        </span>
                                                    </div>
                                                </td>
                                            ))}
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="text-center py-12 bg-white rounded-lg border border-dashed">
                        <Info className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                        <h3 className="text-lg font-medium text-gray-900">Seleccione un Plan</h3>
                        <p className="text-gray-500">Seleccione un plan maestro arriba para ver sus detalles</p>
                    </div>
                )
            }
        </div >
    );
}
